import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { RefPriceOrder } from '../../models/response/RefPrice';
import { Division } from '../components/division-input';
import Cascader from '../components/cascader';
import cascaderOptions, { DivisionUtil } from '@pansy/china-division';

const divisionUtil = new DivisionUtil(cascaderOptions);

// 扩展 RefPriceOrder 类型，添加前端使用的省市区选择字段
interface ExtendedRefPriceOrder extends Partial<RefPriceOrder> {
  fromDivision?: Division;
  toDivision?: Division;
}

interface NewReferencePriceDialogProps {
  open: boolean;
  onClose: () => void;
  refPriceOrder?: RefPriceOrder;
}

const initialFormState: ExtendedRefPriceOrder = {
  order: '',
  price: 0,
  price_type: 0,
  from: '',
  to: '',
  truck_length: undefined,
  truck_type: undefined,
  distance_linear: 0,
  fromDivision: undefined,
  toDivision: undefined,
};

// 货车类型选项
const truckTypeOptions = [
  { id: 0, name: '平板' },
  { id: 1, name: '高栏' },
  { id: 2, name: '厢式' },
  { id: 3, name: '自卸' },
  { id: 4, name: '冷藏' },
  { id: 5, name: '爬梯' },
  { id: 6, name: '集装箱' },
  { id: 7, name: '高低板' },
];

// 货车长度选项
const truckLengthOptions = [
  { id: 0, length: 4.2, name: '4米2' },
  { id: 1, length: 6.8, name: '6米8' },
  { id: 2, length: 8.7, name: '8米7' },
  { id: 3, length: 9.6, name: '9米6' },
  { id: 4, length: 13, name: '13米' },
  { id: 5, length: 13.75, name: '13米75' },
  { id: 6, length: 17.5, name: '17米5' },
];

const EditReferencePriceDialog: React.FC<NewReferencePriceDialogProps> = ({ open, onClose, refPriceOrder }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [formValues, setFormValues] = useState<ExtendedRefPriceOrder>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // 从地址字符串中提取省市区信息的辅助函数
  const getDivisionFromAddress = (address: string): Division | undefined => {
    // 尝试从地址字符串中提取省市区信息
    // 这里假设地址格式为：省份+城市+区县，例如："广东省广州市天河区"
    try {
      // 获取所有省份
      const provinces = divisionUtil.getSourceData();
      
      // 查找匹配的省份
      for (const province of provinces) {
        if (address.includes(province.label)) {
          // 获取该省份下的所有城市
          const cities = province.children || [];
          
          // 查找匹配的城市
          for (const city of cities) {
            if (address.includes(city.label)) {
              // 获取该城市下的所有区县
              const districts = city.children || [];
              
              // 查找匹配的区县
              for (const district of districts) {
                if (address.includes(district.label)) {
                  // 返回匹配的省市区信息
                  return {
                    province: province.label,
                    city: city.label,
                    district: district.label
                  };
                }
              }
              
              // 如果没有找到匹配的区县，返回省市信息，区县为空
              return {
                province: province.label,
                city: city.label,
                district: ''
              };
            }
          }
          
          // 如果没有找到匹配的城市，返回省份信息，城市和区县为空
          return {
            province: province.label,
            city: '',
            district: ''
          };
        }
      }
      
      // 如果没有找到匹配的省份，返回 undefined
      return undefined;
    } catch (error) {
      console.error('解析地址失败:', error);
      return undefined;
    }
  };
  
  // 当 refPriceOrder 参数变化时，更新表单数据
  useEffect(() => {
    console.log('参考价信息', refPriceOrder);
    if (refPriceOrder) {
      // 从地址字符串中提取省市区信息
      const fromDivision = refPriceOrder.from ? getDivisionFromAddress(refPriceOrder.from) : undefined;
      const toDivision = refPriceOrder.to ? getDivisionFromAddress(refPriceOrder.to) : undefined;
      
      setFormValues({
        ...refPriceOrder,
        fromDivision,
        toDivision
      });
    } else {
      setFormValues(initialFormState);
    }
  }, [refPriceOrder]);

  const handleChange = (name: keyof ExtendedRefPriceOrder, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除该字段的错误
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formValues.price || formValues.price <= 0) {
      newErrors.price = '请输入有效的价格';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      
      if (refPriceOrder && formValues.price) {
        refPriceOrder.price = formValues.price;
        // 更新已有参考价格
        await dataProvider.addCargoPrice(refPriceOrder);
        notify('参考价格更新成功', { type: 'success' });
      }
      
      refresh();
      handleClose();
    } catch (error) {
      console.error(refPriceOrder ? '更新参考价格失败:' : '添加参考价格失败:', error);
      notify(refPriceOrder ? '更新参考价格失败' : '添加参考价格失败', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormValues(initialFormState);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{refPriceOrder ? '编辑参考价格' : '添加参考价格'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* 可编辑部分 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
              可编辑信息
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="参考价格(元)"
              type="number"
              fullWidth
              value={formValues.price}
              onChange={(e) => handleChange('price', Number(e.target.value))}
              error={!!errors.price}
              helperText={errors.price}
              required
              InputProps={{ inputProps: { min: 0 } }}
              sx={{ mb: 2 }}
            />
          </Grid>
          
          {/* 不可编辑部分 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 2 }}>
              参考信息（不可编辑）
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>起点</Typography>
            <TextField
              fullWidth
              value={formValues.from || ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>终点</Typography>
            <TextField
              fullWidth
              value={formValues.to || ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>直线距离(公里)</Typography>
            <TextField
              fullWidth
              value={Number(((formValues.distance_linear ?? 0) * 0.001).toFixed(2))}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>货车类型</Typography>
            <TextField
              fullWidth
              value={formValues.truck_type !== undefined ? truckTypeOptions.find(option => option.id === formValues.truck_type)?.name || '' : ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>货车长度</Typography>
            <TextField
              fullWidth
              value={formValues.truck_length !== undefined ? truckLengthOptions.find(option => option.id === formValues.truck_length)?.name || '' : ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">取消</Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : refPriceOrder ? '更新' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditReferencePriceDialog;
